import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  DefaultButton,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  Separator,
  Spinner,
  SpinnerSize,
  Text,
  Toggle
} from '@fluentui/react';
import { Dropdown, Option } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// components
import PersonSelectorComponent from '../../../commons/component/Inputs/PersonSelector/PersonSelector.Component';
import ContractsSelectorComponent from '../../../commons/component/Inputs/ContractSelector/ContractSelectorComponent';
import ProtocolContractFormComponent from '../Protocols/ProtocolContractForm/ProtocolContractFormComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import PractitionerConceptsListComponent from '../../Protocols/Practitioners/PracititionerConceptsList/PractitionerConcepts.List.Component';

// models
import { Person } from '../../../models/Entities/Person/Person';
import { ContractCommissionAgent } from '../../../models/Entities/Contracts/ContractCommissionAgent';
import { NewContractCommissionAgent } from '../../../models/Entities/Contracts/NewContractCommissionAgent';
import {
  CommissionAgentTypeEnum,
  CommissionAgentTypeOptions
} from '../../../models/Entities/Contracts/CommissionAgentType';

// functions
import * as ownFunctions from './ContractCommissionAgent.Form.Functions';

/* Se reusa la hoja del formulario del equipo del protocolo a propósito: los dos
   paneles tienen que verse iguales, y compartiendo los estilos no se despegan
   cuando alguno se toque. */
import '../../Protocols/Practitioners/PractitionerSettingsForm/PractitionerSettingsForm.Styles.css';

const EmptyIdentifier = '00000000-0000-0000-0000-000000000000';

const ContractCommissionAgentFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const isEditing = props.Agent?.id !== undefined;

  const [submitted, setSubmitted] = useState<boolean>(false);

  const [agentType, setAgentType] = useState<CommissionAgentTypeEnum>(
    CommissionAgentTypeEnum.Practitioner
  );
  const [personIdentifier, setPersonIdentifier] = useState<string>('');
  const [agentName, setAgentName] = useState<string>('');
  const [payPercentage, setPayPercentage] = useState<boolean>(true);
  const [amount, setAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('USD');
  const [onlyDoneHimSelf, setOnlyDoneHimSelf] = useState<boolean>(false);

  /* Contrato asociado: lo mismo que ofrece el panel del equipo del protocolo.
     Cuando el colaborador liquida contra otro contrato, sus conceptos salen de
     ahí y por eso esta pantalla los deja en sólo lectura. */
  const [haveContractAssociated, setHaveContractAssociated] = useState<boolean>(false);
  const [associatedContractId, setAssociatedContractId] = useState<number | undefined>(undefined);
  const [isContractPanelOpen, setIsContractPanelOpen] = useState<boolean>(false);

  /* Editando se parte de lo que el comisionista ya tiene. El tipo puede venir
     vacío —los agentes cargados antes de que la API tuviera el campo no lo
     traen— y en ese caso es un profesional, que era lo único que existía. */
  useEffect(() => {
    if (!props.isOpen) return;

    if (props.Agent) {
      setAgentType(props.Agent.commissionAgentType ?? CommissionAgentTypeEnum.Practitioner);
      setPersonIdentifier(props.Agent.commissionAgentId || '');
      setAgentName(props.Agent.commissionAgentName || '');
      setPayPercentage(props.Agent.isCommissionAPercentage);
      setAmount(props.Agent.commissionAmount);
      setCurrency(props.Agent.currency || 'USD');
      setOnlyDoneHimSelf(props.Agent.onlyDoneHimSelf);
      setHaveContractAssociated(props.Agent.haveContractAssociated || false);
      setAssociatedContractId(props.Agent.contractAssociatedId);
    } else {
      resetForm();
    }
  }, [props.isOpen, props.Agent?.id]);

  useEffect(() => {
    if (submitted && (props.isSavedSuccessfully || props.isUpdatedSuccessfully)) {
      setSubmitted(false);
      resetForm();
      props.dismissPanel();
    }
  }, [props.isSavedSuccessfully, props.isUpdatedSuccessfully]);

  const resetForm = () => {
    setAgentType(CommissionAgentTypeEnum.Practitioner);
    setPersonIdentifier('');
    setAgentName('');
    setPayPercentage(true);
    setAmount(0);
    setCurrency('USD');
    setOnlyDoneHimSelf(false);
    setHaveContractAssociated(false);
    setAssociatedContractId(undefined);
  };

  /* Una persona sin identificador único no puede ser comisionista: ese
     identificador ES lo que el contrato guarda para nombrarla. Se avisa acá en
     vez de dejar guardar y que la API rechace algo que el usuario no puede
     relacionar con lo que eligió. */
  const hasValidPerson = !!personIdentifier && personIdentifier !== EmptyIdentifier;
  const canSave = hasValidPerson && amount >= 0 && !props.isSaving && !props.isUpdating;

  const handlePersonChange = (selected: Person[]) => {
    const person = selected[0];
    setPersonIdentifier(person?.personIdentifier || '');
    setAgentName(person?.businessName || '');
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;

    const payload = {
      contractId: props.ContractId,
      commissionAgentId: personIdentifier,
      commissionAgentName: agentName,
      commissionAgentType: agentType,
      currency: currency,
      commissionAmount: amount,
      isCommissionAPercentage: payPercentage,
      onlyDoneHimSelf: onlyDoneHimSelf,
      haveContractAssociated: haveContractAssociated,
      contractAssociatedId: haveContractAssociated ? associatedContractId : undefined
    };

    if (isEditing) {
      props.UpdateContractCommissionAgent({
        ...payload,
        id: props.Agent!.id
      } as ContractCommissionAgent);
    } else {
      props.CreateContractCommissionAgent(payload as NewContractCommissionAgent);
    }
  };

  const handleCancel = () => {
    setSubmitted(false);
    resetForm();
    props.dismissPanel();
  };

  const selectedTypeOption = CommissionAgentTypeOptions.find((option) => option.key === agentType);
  const typeLabel = selectedTypeOption
    ? String(t(selectedTypeOption.textKey, { defaultValue: selectedTypeOption.defaultText }))
    : '';

  /* El footer lo dibuja el panel, como en el formulario del equipo del protocolo:
     así los botones quedan fijos abajo y no se pierden al final de un panel largo
     —que es exactamente lo que pasa cuando el colaborador tiene varios conceptos—. */
  const onRenderFooterContent = () => (
    <div className="PractitionerSettingsFooter">
      <DefaultButton
        text={t('contractCommissionAgents.form.cancel', { defaultValue: 'Cancelar' })}
        onClick={handleCancel}
        disabled={props.isSaving || props.isUpdating}
      />
      <PrimaryButton
        text={t('contractCommissionAgents.form.save', { defaultValue: 'Guardar' })}
        onClick={handleSave}
        disabled={!canSave}
      />
      {(props.isSaving || props.isUpdating) && <Spinner size={SpinnerSize.small} />}
    </div>
  );

  return (
    <Panel
      isOpen={props.isOpen}
      onDismiss={handleCancel}
      type={PanelType.medium}
      isLightDismiss={true}
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      className="PractitionerSettingsPanel"
      headerText={
        isEditing
          ? agentName ||
            t('contractCommissionAgents.form.editTitle', { defaultValue: 'Modificar colaborador' })
          : t('contractCommissionAgents.form.addTitle', { defaultValue: 'Agregar colaborador' })
      }
      closeButtonAriaLabel={t('contractCommissionAgents.form.close', { defaultValue: 'Cerrar' })}
    >
      {/* Debajo del nombre, qué clase de colaborador es: el mismo lugar donde el
          panel del profesional pone su rol en el estudio. */}
      {isEditing && !!typeLabel && (
        <div className="PractitionerSettingsHeaderMeta">
          <Text className="TextDecorated">{typeLabel}</Text>
        </div>
      )}

      <Separator className="PractitionerSettingsSeparator" />

      <div className="PractitionerSettingsContent">
        {submitted && (props.failOnSaving || props.failOnUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div className="PractitionerSettingsFormBody">
          {/* Contrato asociado: la misma sección, en el mismo lugar y con los
              mismos controles que el panel del equipo del protocolo. */}
          <section className="PractitionerSettingsSection">
            <div className="PractitionerSettingsSectionHeader">
              <Text variant="mediumPlus" className="PractitionerSettingsSectionTitle">
                {t('contractAssociation.formValidation.selectContract', 'Debe seleccionar un contrato')}
              </Text>
              <Text className="PractitionerSettingsSectionDescription">
                {t(
                  'practitionerSettingsForm.contractAssociationHint',
                  'Active la asociacion de contrato para vincular una liquidacion existente con este profesional.'
                )}
              </Text>
            </div>

            <div className="PractitionerSettingsToggleRow">
              <Toggle
                label={'Contrato asociado'}
                checked={haveContractAssociated}
                onChange={(_event, checked) => setHaveContractAssociated(checked || false)}
              />
            </div>

            <div className="PractitionerSettingsContractSelector">
              <ContractsSelectorComponent
                onSelectedContract={(contracts: any[]) => setAssociatedContractId(contracts[0]?.id)}
                ContractsSelected={associatedContractId ? [associatedContractId] : undefined}
                MultiselectEnable={false}
                disabled={!haveContractAssociated}
              />
            </div>

            <div className="PractitionerSettingsActionRow">
              <PrimaryButton
                text={'Agregar contrato'}
                onClick={() => setIsContractPanelOpen(true)}
                disabled={props.isSaving || props.isUpdating || !haveContractAssociated}
              />
            </div>
          </section>

          {/* Persona y tipo aparecen sólo al dar de alta: son los datos que crean
              al colaborador y sin ellos no hay a quién guardar. Editando, el panel
              muestra lo mismo que el del equipo del protocolo —contrato asociado y
              conceptos— y nada más. */}
          {!isEditing && (
            <section className="PractitionerSettingsSection">
              <div className="PractitionerSettingsSectionHeader">
                <Text variant="mediumPlus" className="PractitionerSettingsSectionTitle">
                  {t('contractCommissionAgents.form.dataTitle', { defaultValue: 'Datos del colaborador' })}
                </Text>
                <Text className="PractitionerSettingsSectionDescription">
                  {t('contractCommissionAgents.form.dataHint', {
                    defaultValue:
                      'Quién es y qué clase de colaborador es. Sale de la lista de personas: el contrato lo guarda por su identificador único.'
                  })}
                </Text>
              </div>

              {/* El tipo va primero: es lo que dice qué clase de comisionista se
                  está cargando —si es un médico o no— y encuadra lo que sigue. */}
              <Dropdown
                className="cca-form__field"
                aria-label={t('contractCommissionAgents.form.type', { defaultValue: 'Tipo de colaborador' })}
                placeholder={t('contractCommissionAgents.form.typePlaceholder', {
                  defaultValue: 'Elegí el tipo'
                })}
                value={
                  selectedTypeOption
                    ? t(selectedTypeOption.textKey, { defaultValue: selectedTypeOption.defaultText })
                    : ''
                }
                selectedOptions={[String(agentType)]}
                onOptionSelect={(_, data) =>
                  setAgentType(Number(data.optionValue) as CommissionAgentTypeEnum)
                }
              >
                {CommissionAgentTypeOptions.map((option) => {
                  const label = String(t(option.textKey, { defaultValue: option.defaultText }));
                  return (
                    /* `text` explícito: sin él Fluent no sabe con qué texto
                       representar a la opción cuando queda elegida, porque el hijo
                       es lo que devuelve la traducción y no una cadena literal. */
                    <Option key={option.key} value={String(option.key)} text={label}>
                      {label}
                    </Option>
                  );
                })}
              </Dropdown>

              {/* Cualquiera sea el tipo, el comisionista es una persona y se
                  identifica con su `personIdentifier`: eso guarda el contrato. */}
              <PersonSelectorComponent
                personSelected={personIdentifier ? [personIdentifier] : []}
                selectionLimit={1}
                isRequired={true}
                fieldLabel={t('contractCommissionAgents.form.person', { defaultValue: 'Persona' })}
                onChangeSelection={handlePersonChange}
              />

              {submitted && !hasValidPerson && (
                <MessageComponent
                  message={t('contractCommissionAgents.form.personRequired', {
                    defaultValue: 'Elegí una persona con identificador único'
                  })}
                  type={MessageBarType.warning}
                />
              )}
            </section>
          )}

          {/* Los conceptos a liquidar, en su propia sección y con el mismo
              encabezado que el panel del equipo del protocolo: es la misma
              pregunta y tiene que leerse igual. Un concepto se carga contra un
              colaborador que ya existe, así que recién aparece cuando hay uno
              para colgárselo: dando de alta todavía no hay id. */}
          <section className="PractitionerSettingsSection">
            <div className="PractitionerSettingsSectionHeader">
              <Text variant="mediumPlus" className="PractitionerSettingsSectionTitle">
                {t('contractDetailPage.contractDetailItem.conceptsSettled', {
                  defaultValue: 'Conceptos a liquidar'
                })}
              </Text>
              <Text className="PractitionerSettingsSectionDescription">
                {t('contractCommissionAgents.concepts.hint', {
                  defaultValue:
                    'Defina los conceptos coparticipables de este colaborador para el contrato.'
                })}
              </Text>
            </div>

        {isEditing ? (
          <PractitionerConceptsListComponent
            ContractId={props.ContractId}
            CommissionAgentId={props.Agent?.id}
            disabled={haveContractAssociated}
            subtitleText={t('contractCommissionAgents.concepts.subtitle', {
              defaultValue:
                'Configure los conceptos coparticipables asociados a este colaborador para el contrato.'
            })}
            emptyDescriptionText={t('contractCommissionAgents.concepts.empty', {
              defaultValue:
                'Agregue conceptos para definir de forma clara la liquidación de este colaborador.'
            })}
          />
        ) : (
          <MessageComponent
            message={t('contractCommissionAgents.concepts.saveFirst', {
              defaultValue:
                'Guardá el colaborador para poder cargarle los conceptos a liquidar.'
            })}
            type={MessageBarType.info}
          />
        )}
          </section>
        </div>
      </div>

      {/* El alta de contrato, igual que en el panel del profesional. */}
      <ProtocolContractFormComponent
        isOpen={isContractPanelOpen}
        Contract={undefined}
        dismissPanel={() => setIsContractPanelOpen(false)}
        openPanel={() => setIsContractPanelOpen(true)}
        mode=""
        RefererId={undefined}
      />
    </Panel>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ContractCommissionAgentFormComponent as any);
